<div class="mx-auto max-w-[860px] px-3 md:px-4 pt-4 pb-12">
    <section tuiCardLarge appearance="outline-grayscale" class="mb-5">
      <p class="text-body text-muted">Paste a Warcraft Logs report to see how your Mythic pulls compare with the top logs for your spec.</p>

      <div class="flex flex-col gap-1">
        <label tuiLabel>
          Warcraft Logs report URL or code
          <tui-textfield iconStart="link">
            <input #reportInput id="report-input" tuiInput [formControl]="reportControl" placeholder="https://www.warcraftlogs.com/reports/AbCdEfGh…"
                   (keydown.enter)="loadReport()" (paste)="onPaste($event, reportInput)" />
          </tui-textfield>
        </label>
        <tui-error [formControl]="reportControl" />
      </div>

      <wl-live-controls></wl-live-controls>

      @if (fights().length) {
        <div class="border-t border-line pt-3">
          <div class="flex flex-wrap gap-3.5">
            <!-- Disabled via the form control while live sync drives the selection, so keyboard changes are blocked too. -->
            <label tuiLabel class="flex-[1_1_200px]">
              Fight
              <tui-textfield tuiChevron [content]="selectedFight() ? fightContent : ''" [stringify]="fightLabel">
                <input tuiSelect [formControl]="fightControl" />
                <tui-data-list *tuiDropdown>
                  @for (f of fights(); track f.id) {
                    <button tuiOption type="button" [value]="f.id">
                      <span class="flex min-w-0 items-center gap-2">
                        <wl-art-icon [src]="f.encounterID | bossIcon" [alt]="f.name"></wl-art-icon>
                        <span class="truncate">{{ f.name }} - {{ f.kill ? 'Kill' : 'Wipe #' + f.attempt }} - {{ f.duration_s | formatDuration }}</span>
                      </span>
                    </button>
                  }
                </tui-data-list>
              </tui-textfield>
            </label>
            <ng-template #fightContent>
              @if (selectedFight(); as sf) {
                <span class="flex min-w-0 items-center gap-2">
                  <wl-art-icon [src]="sf.encounterID | bossIcon" [alt]="sf.name"></wl-art-icon>
                  <span class="truncate">{{ sf.name }} - {{ sf.kill ? 'Kill' : 'Wipe #' + sf.attempt }} - {{ sf.duration_s | formatDuration }}</span>
                </span>
              }
            </ng-template>

            <label tuiLabel class="flex-[1_1_200px]">
              Player
              <tui-textfield tuiChevron [content]="selectedPlayer() ? playerContent : ''" [stringify]="playerLabel">
                <input tuiSelect [formControl]="playerControl" />
                <tui-data-list *tuiDropdown>
                  @for (p of visiblePlayers(); track p.id) {
                    <button tuiOption type="button" [value]="p.id">
                      <span class="flex min-w-0 items-center gap-2">
                        @if (playerSpecs()[p.id]; as specFolder) {
                          <wl-art-icon [src]="specFolder | specIcon" [alt]="specFolder | formatSpec"></wl-art-icon>
                        } @else {
                          <wl-art-icon [src]="p.spec | classIcon" [alt]="p.spec"></wl-art-icon>
                        }
                        <span class="truncate">{{ p.name }}</span>
                      </span>
                    </button>
                  }
                </tui-data-list>
              </tui-textfield>
            </label>
            <ng-template #playerContent>
              @if (selectedPlayer(); as sp) {
                <span class="flex min-w-0 items-center gap-2">
                  @if (playerSpecs()[sp.id]; as specFolder) {
                    <wl-art-icon [src]="specFolder | specIcon" [alt]="specFolder | formatSpec"></wl-art-icon>
                  } @else {
                    <wl-art-icon [src]="sp.spec | classIcon" [alt]="sp.spec"></wl-art-icon>
                  }
                  <span class="truncate">{{ sp.name }}</span>
                </span>
              }
            </ng-template>
          </div>
        </div>
      }
    </section>

    @if (notice()) {
      <div tuiNotification appearance="neutral" size="m" class="mb-4">{{ notice() }}</div>
    }

    @if (loadError(); as err) {
      <wl-load-state class="mb-4" [error]="err"></wl-load-state>
    }

    @if (loadingReport() || loadingAnalysis() || (ready() && cardsBusy())) {
      <wl-loading-spinner [message]="loadingMsg()"></wl-loading-spinner>
    }

    <!-- @defer only splits the bundle; it never un-renders, so the @if owns visibility. -->
    @defer (on idle) {
      @if (ready() && !loadingAnalysis()) {
        <!-- Mounted but hidden via display:none until loaded: @if-gating on cardsBusy() would unmount the cards mid-load. -->
        <div class="flex flex-col gap-6" [class.hidden]="cardsBusy()">
          @if (!benchAvailable()) {
            <wl-bench-empty-banner variant="post" [encounter]="selectedFight()?.name ?? ''"></wl-bench-empty-banner>
          }

          <wl-pull-overview
            [report]="reportCode()"
            [fight]="selectedFight()!"
            [player]="selectedPlayerId()!"
            [showMap]="mapReady()"
            [showClip]="clipReady()"
            (openMap)="onOpenMap($event)"
            (openClip)="onOpenClip($event)"
            (busyChange)="cards.setBusy('pullOverview', $event)">
          </wl-pull-overview>

          <wl-rotation
            [spec]="spec()"
            [encounterId]="selectedEncounterId()"
            [reportCode]="reportCode()"
            [fightId]="selectedFightId()!"
            [playerId]="selectedPlayerId()!"
            [showMap]="mapReady()"
            [showClip]="clipReady()"
            (openMap)="onOpenMap($event)"
            (openClip)="onOpenClip($event)"
            (busyChange)="cards.setBusy('rotation', $event)"
            (availableChange)="cards.setAvailable('rotation', $event)">
          </wl-rotation>

          <wl-burst-windows
            [spec]="spec()"
            [encounterId]="selectedEncounterId()"
            [report]="reportCode()"
            [fight]="selectedFightId()!"
            [player]="selectedPlayerId()!"
            [showMap]="mapReady()"
            [showClip]="clipReady()"
            (openMap)="onOpenMap($event)"
            (openClip)="onOpenClip($event)"
            (busyChange)="cards.setBusy('burst', $event)"
            (availableChange)="cards.setAvailable('burst', $event)">
          </wl-burst-windows>

          <wl-defensive
            [spec]="spec()"
            [encounterId]="selectedEncounterId()"
            [report]="reportCode()"
            [fight]="selectedFightId()!"
            [player]="selectedPlayerId()!"
            [showMap]="mapReady()"
            [showClip]="clipReady()"
            (openMap)="onDefensiveOpenMap($event)"
            (openClip)="onOpenClip($event)"
            (busyChange)="cards.setBusy('defensive', $event)"
            (availableChange)="cards.setAvailable('defensive', $event)">
          </wl-defensive>

          <wl-gear
            [spec]="spec()"
            [encounterId]="selectedEncounterId()"
            [report]="reportCode()"
            [fight]="selectedFightId()!"
            [player]="selectedPlayerId()!"
            (busyChange)="cards.setBusy('gear', $event)"
            (availableChange)="cards.setAvailable('gear', $event)">
          </wl-gear>
        </div>
      }
    }

    <wl-map-panel></wl-map-panel>
    <wl-clip-panel></wl-clip-panel>
</div>
